import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ActivityEntry {
  id: number;
  type: 'success' | 'error';
  action: 'create' | 'update' | 'delete' | 'action';
  message: string;
  displayMessage: string;
  /** The module/page the user was on when this notification was triggered (e.g. 'Campaigns', 'Customers') */
  module?: string;
  /** Optional secondary context line (e.g. "Member: John Doe • Card: #0012345") */
  details?: string;
  /** Optional Material icon name override */
  icon?: string;
  /** Optional raw value to expose a copy-to-clipboard button (e.g. the member's QC code) */
  copyValue?: string;
  timestamp: Date;
  read: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActivityLogService {
  private entries: ActivityEntry[] = [];
  private idCounter = 0;

  entries$ = new BehaviorSubject<ActivityEntry[]>([]);
  unreadCount$ = new BehaviorSubject<number>(0);

  addEntry(
    type: 'success' | 'error',
    action: ActivityEntry['action'],
    message: string,
    details?: string,
    icon?: string,
    copyValue?: string,
    module?: string,
  ): void {
    const entry: ActivityEntry = {
      id: ++this.idCounter,
      type,
      action,
      message,
      displayMessage: this.enrichMessage(type, action, message),
      module,
      details,
      icon,
      copyValue,
      timestamp: new Date(),
      read: false,
    };

    this.entries = [entry, ...this.entries].slice(0, 50);
    this.entries$.next(this.entries);
    this.updateUnreadCount();
  }

  markAllRead(): void {
    this.entries = this.entries.map((e) => ({ ...e, read: true }));
    this.entries$.next(this.entries);
    this.updateUnreadCount();
  }

  clearAll(): void {
    this.entries = [];
    this.entries$.next(this.entries);
    this.updateUnreadCount();
  }

  private enrichMessage(type: 'success' | 'error', action: ActivityEntry['action'], message: string): string {
    // If the message is already descriptive (not a generic literal), use it as-is
    const genericMessages = [
      'saved',
      'your changes have been saved',
      'changes saved',
      'your changes have not been saved',
      'failed',
      'error',
    ];

    const isGeneric = genericMessages.some((g) => message.toLowerCase().trim() === g);

    if (!isGeneric) {
      return message;
    }

    // Enrich generic messages based on action type and success/failure
    if (type === 'success') {
      switch (action) {
        case 'create':
          return 'Record created successfully';
        case 'update':
          return 'Record updated successfully';
        case 'delete':
          return 'Record deleted successfully';
        default:
          return 'Operation completed successfully';
      }
    } else {
      switch (action) {
        case 'create':
          return 'Failed to create record';
        case 'update':
          return 'Failed to update record';
        case 'delete':
          return 'Failed to delete record';
        default:
          return 'Operation failed';
      }
    }
  }

  private updateUnreadCount(): void {
    this.unreadCount$.next(this.entries.filter((e) => !e.read).length);
  }
}
